import axios from 'axios'
import { getIdToken, getIdTokenForUid } from '@/utils/auth'
import { API_BASE } from '@/services/api'
import { autoSyncLayoutWarning } from '@/utils/syncStatus'

export interface UserSpreadsheet {
  spreadsheetId: string
  title: string
  url: string
  createdAt?: string // ISO-8601 — backend serializes Firestore Timestamp at the wire
  templateId?: string | null
  syncWarning?: string
}

export interface BankAccount {
  accountId: string
  accountName: string
  accountNumber: string
  accountType: string
  accountSubtype: string
  institutionName: string
  itemId: string
}

export interface SyncPreference {
  frequency: string
  savedAt?: string // ISO-8601 — backend serializes Firestore Timestamp at the wire
  lastSync?: number
  enableAutomaticSyncing?: boolean
  spreadsheetId?: string
  lastSuccessfulSync?: string // ISO-8601 — backend serializes Firestore Timestamp at the wire
  selectedAccountIds?: string[]
  exportType?: string
  lastSyncedTabs?: string[]
  itemState?: Record<string, { lastErrorCode?: 'sheet_layout_mismatch' }>
}

export interface UserData {
  googleSheets?: {
    spreadsheets?: UserSpreadsheet[]
    /**
     * Legacy single-slot sheet id. Still set on accounts that have not created a
     * sheet since sheets.service.ts gained its lazy migration, so it is the only
     * evidence those users have a spreadsheet at all.
     */
    bankSheetsTemplateSpreadsheetId?: string
  }
  bankAccounts?: BankAccount[]
  syncPreferences?: SyncPreference[]
  /**
   * ISO-8601 — when the user last finished V1 onboarding. Absent means they have not.
   * Read by V1Entry to decide between the dashboard and onboarding.
   */
  onboardingCompletedAt?: string
}

/**
 * Fetch the user's full userData document via the backend. Returns null
 * if the document doesn't exist. All other functions in this file that
 * previously read userData directly now go through this single call.
 *
 * The backend derives the uid from the Firebase ID token; the `userId`
 * parameter is preserved for signature compatibility with callers but
 * is not sent over the wire — the backend always uses the authenticated
 * user's uid.
 */
/** What a connection says about itself, as opposed to about its accounts. */
export interface BankConnection {
  itemId: string
  status: 'active' | 'requires_reauth' | 'disconnected' | 'unknown'
  reconnectMode?: 'update' | 'relink'
  institutionName?: string
}

const requestUserDataResponse = async (
  signal?: AbortSignal,
  expectedUid?: string
): Promise<{
  userData: UserData | null
  banks?: BankConnection[]
  bankStatusesAvailable?: boolean
}> => {
  const token = expectedUid ? await getIdTokenForUid(expectedUid) : await getIdToken()
  const { data } = await axios.get<{
    userData: UserData | null
    banks?: BankConnection[]
    bankStatusesAvailable?: boolean
  }>(`${API_BASE}/userApi/v1/data`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  })
  return data
}

const requestUserData = async (signal?: AbortSignal): Promise<UserData | null> =>
  (await requestUserDataResponse(signal)).userData

/**
 * The accounts and their connections' statuses, from one request. A status
 * failure is returned explicitly rather than being interpreted as connected.
 *
 * Separate from requestUserBankAccounts because almost nothing needs the second
 * half: only the page that has to tell a working bank from a revoked one. Both
 * come from the same response either way, so this costs no extra read.
 *
 * Throws, like requestUserBankAccounts and for the same reason — an empty list
 * must not be mistaken for "this user has no banks".
 */
export const requestUserBanks = async (
  signal?: AbortSignal
): Promise<{
  accounts: BankAccount[]
  connections: BankConnection[]
  statusesAvailable: boolean
}> => {
  const data = await requestUserDataResponse(signal)
  return {
    accounts: data.userData?.bankAccounts || [],
    connections: data.banks || [],
    statusesAvailable: data.bankStatusesAvailable !== false,
  }
}

/**
 * Swallowing wrapper, kept for the readers whose callers treat a missing value as
 * "absent" rather than "failed" — sync preferences, connection checks, and the V0
 * bank-account reads. For those, null and an empty document are genuinely the same
 * answer.
 *
 * It is NOT suitable for anything a user is shown a list of: `null` here collapses
 * a network error, an expired token and a 500 into the same value as an empty
 * account, so the UI renders "you have nothing" when the truth is "we could not
 * tell". Readers in that position use requestUserData and let the error through.
 */
const fetchUserDataFromApi = async (): Promise<UserData | null> => {
  try {
    return await requestUserData()
  } catch (error) {
    console.error('Error fetching user data:', error)
    return null
  }
}

/**
 * Fetch user's bank accounts from the userData document.
 *
 * Swallowing: a failure comes back as an empty list. Fine for the V0 callers, which
 * use it to decide what to render. NOT fine where an empty list triggers an action
 * — see requestUserBankAccounts.
 */
export const fetchUserBankAccounts = async (
  _userId: string,
  expectedUid?: string
): Promise<BankAccount[]> => {
  if (expectedUid) {
    try {
      return (await requestUserDataResponse(undefined, expectedUid)).userData?.bankAccounts || []
    } catch (error) {
      console.error('Error fetching user bank accounts:', error)
      return []
    }
  }
  const data = await fetchUserDataFromApi()
  return data?.bankAccounts || []
}

/**
 * The same read, but a failed request throws instead of returning an empty list.
 *
 * Needed wherever "no accounts" drives behaviour rather than just display. On the
 * bank-intro page an empty list means the user is offered a fresh Plaid connection
 * — and the initial exchange REPLACES the stored token and bankAccounts rather
 * than adding to them. Routed through the swallowing reader, a network blip or an
 * expired token is indistinguishable from a genuinely empty account list, so
 * following that prompt after a transient failure silently destroys a working
 * connection.
 *
 * Kept separate rather than making fetchUserBankAccounts throw: V0's UserFlow and
 * Dashboard read it to decide what to render, where an empty list is a display
 * decision and swallowing is the behaviour they were written against.
 */
export const requestUserBankAccounts = async (
  signal?: AbortSignal,
  expectedUid?: string
): Promise<BankAccount[]> => {
  const data = expectedUid
    ? await requestUserDataResponse(signal, expectedUid).then((response) => response.userData)
    : await requestUserData(signal)
  return data?.bankAccounts || []
}

/**
 * Fetch user's spreadsheets from the userData document.
 */
/**
 * Throws on a failed request rather than returning an empty list — the dashboard
 * shows this to the user, and "we could not load your sheets" must not look like
 * "you have no sheets". The only caller (useUserSpreadsheets) already catches.
 */
export const fetchUserSpreadsheets = async (_userId: string): Promise<UserSpreadsheet[]> => {
  const data = await requestUserData()
  const spreadsheets = data?.googleSheets?.spreadsheets || []
  return spreadsheets.map((sheet) => {
    const syncWarning = autoSyncLayoutWarning(
      data?.syncPreferences?.find((sync) => sync.spreadsheetId === sheet.spreadsheetId)
    )
    return { ...sheet, ...(syncWarning ? { syncWarning } : {}) }
  })
}

/**
 * Persist the V1 onboarding profile: account nicknames and the referral answer.
 *
 * Both fields are optional and independently saved — the backend merges nicknames
 * key-by-key, so sending one bank's accounts cannot erase another's, and an
 * omitted field is left untouched.
 *
 * Callers should not block onboarding on this. The values stay in context for the
 * current pass, and nothing in this session reads them back, so a failure only
 * costs the saved names on a future visit.
 */
export const saveOnboardingProfile = async (
  input: {
    accountNicknames?: Record<string, string>
    referralSource?: string
    /** Records that onboarding finished. The server stamps the time. */
    onboardingComplete?: boolean
  },
  signal?: AbortSignal
): Promise<void> => {
  try {
    const token = await getIdToken()
    await axios.post(`${API_BASE}/userApi/v1/onboardingProfile`, input, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    })
  } catch (error) {
    console.error('Error saving onboarding profile:', error)
    throw error
  }
}

/**
 * Check if user has already saved sync preferences.
 */
export const hasSyncPreferences = async (_userId: string): Promise<boolean> => {
  const data = await fetchUserDataFromApi()
  return !!data?.syncPreferences
}

/**
 * Get list of spreadsheet IDs that have auto-sync enabled.
 */
export const getSheetsWithAutoSync = async (_userId: string): Promise<string[]> => {
  const data = await fetchUserDataFromApi()
  if (!data) return []
  if (Array.isArray(data.syncPreferences)) {
    return data.syncPreferences
      .map((pref) => pref.spreadsheetId)
      .filter((id): id is string => Boolean(id))
  }
  // Fallback for legacy single-object shape
  // @ts-expect-error: legacy support
  return data.syncPreferences?.spreadsheetId ? [data.syncPreferences.spreadsheetId] : []
}

/**
 * Get sync preferences including tab information.
 */
export const getSyncPreferences = async (_userId: string) => {
  const data = await fetchUserDataFromApi()
  return data?.syncPreferences || null
}

interface ConnectionStatus {
  hasAnyValidRefreshToken: boolean
  plaid: boolean
  google: boolean
}

/**
 * Fetch connection status once per call — Plaid and Google refresh token
 * validity. Replaces three separate direct-Firestore reads of userTokens.
 */
export const fetchConnectionStatus = async (): Promise<ConnectionStatus> => {
  try {
    const token = await getIdToken()
    const { data } = await axios.get<ConnectionStatus>(`${API_BASE}/userApi/v1/connectionStatus`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return data
  } catch (error) {
    console.error('Error checking refresh token validity:', error)
    return { hasAnyValidRefreshToken: false, plaid: false, google: false }
  }
}

/**
 * Check if user has any valid long-term refresh token (Plaid or Google).
 */
export const hasValidRefreshToken = async (_userId: string): Promise<boolean> => {
  const status = await fetchConnectionStatus()
  return status.hasAnyValidRefreshToken
}

/**
 * Check if user has a valid Plaid long-term refresh token.
 */
export const hasValidPlaidRefreshToken = async (_userId: string): Promise<boolean> => {
  const status = await fetchConnectionStatus()
  return status.plaid
}

/**
 * Check if user has a valid Google refresh token.
 */
export const hasValidGoogleRefreshToken = async (_userId: string): Promise<boolean> => {
  const status = await fetchConnectionStatus()
  return status.google
}

/**
 * Fetch the entire userData document for the authenticated user.
 */
/**
 * Throws on a failed request. Both callers (v0/components/MySheets and
 * pages/Sheets) already wrap this in try/catch — MySheets even renders a
 * fetchError message that could never fire while this swallowed.
 */
export const fetchUserData = async (
  _userId: string,
  signal?: AbortSignal
): Promise<UserData | null> => {
  return requestUserData(signal)
}

/**
 * Delete the authenticated user's account data.
 *
 * The server attempts provider revocation, records unresolved cleanup, and deletes app data and Firebase Auth.
 * Callers only sign out locally after success. Stripe history and Drive files stay.
 * The durable deletion request can resume after a failed or interrupted response.
 */
export const deleteAccount = async (): Promise<void> => {
  try {
    const token = await getIdToken()
    await axios.delete(`${API_BASE}/userApi/v1/account`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch (error) {
    console.error('Error deleting account data:', error)
    if (error instanceof Error) {
      throw new Error(`Failed to delete account: ${error.message}`)
    }
    throw new Error('Failed to delete account data')
  }
}
